/**
 * TraceKeyTranscript — a run's transcript reached by its full R2 trace key.
 *
 * The door for links that only carry a key (TRA-939 follow-up): most
 * production traffic writes conversation-less keys, so the trajectories
 * page can't always link into the conversation view. The trajectory
 * endpoint returns the complete run either way: the whole thread for a
 * conversation-bearing key, the one document otherwise.
 */

import { getApiErrorStatus } from '@/api/client'
import { useGetTrajectoryApiTracesTrajectoryTraceKeyGet } from '@/api/generated/traces/traces'

import { parseTraceKey } from './traceKey'
import { TrajectoryTranscript } from './TrajectoryTranscript'
import { transcriptLoadError } from './transcriptLoadError'

interface TraceKeyTranscriptProps {
  traceKey: string
}

export function TraceKeyTranscript({
  traceKey,
}: TraceKeyTranscriptProps): React.JSX.Element {
  const parsed = parseTraceKey(traceKey)
  const query = useGetTrajectoryApiTracesTrajectoryTraceKeyGet(traceKey, {
    query: { enabled: parsed !== null },
  })
  if (parsed === null) {
    return (
      <p className="border border-dashed border-destructive/40 p-6 text-sm text-destructive">
        This link's trace key is malformed. Open the run from the
        Trajectories page instead.
      </p>
    )
  }
  const data = query.data
  const arrived = data?.status === 200
  const docs = arrived ? data.data : null
  const lastDoc = docs?.[docs.length - 1]
  return (
    <TrajectoryTranscript
      // A conversation-less run has no conversation id; the trace id is the
      // stable handle people paste into review links and log searches.
      idLabel={parsed.conversationId === null ? 'Trace' : 'Conversation'}
      idValue={parsed.conversationId ?? lastDoc?.traceId ?? ''}
      docs={docs}
      loadError={transcriptLoadError(
        query.isError || (data !== undefined && !arrived),
        getApiErrorStatus(query.error),
      )}
    />
  )
}
